import React, { Fragment, useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Dialog, Transition } from '@headlessui/react';

import SelectContactsPane from './SelectContactsPane';
import ComposeMessageModal from './ComposeMessageModal';
import IntroContextPane from './IntroContextPane';
import TEMPLATE_BODY from '../resources/DefaultEmailBody.txt';
import TEMPLATE_SUBJECT from '../resources/DefaultEmailSubject.txt';
import Icon from './Icon';
import { useServices } from '../contexts/ServiceContext';

/**
 * Used when user wants to create Intro
 */
export default function IntroModal({
  userData,
  isOpen,
  onClose,
  contacts,
  onNewContactSubmit,
  onIntroEditorSubmit,
  setIntroModal,
  setShowSpinner,
}) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [templateText, setTemplateText] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [currentPane, setCurrentPane] = useState('select-contacts');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [errorMessage, setErrorMessage] = useState(
    'Something wrong with the g-diffuser'
  ); // 1. New State

  // Persisted state for the Context Pane
  const [introContext, setIntroContext] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(undefined);

  const [showSelectContacts, setShowSelectContacts] = useState(true);
  const [showComposeIntro, setShowComposeIntro] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const { templateService, introductionService } = useServices();

  /**
   * When we finish selection of two contacts
   */
  const onFinishSelection = (contactOne, contactTwo) => {
    setSelectedContacts([contactOne, contactTwo]);
    setCurrentPane('intro-context');
  };

  /**
   * When we finish providing context
   * Updated to capture both text and the template object
   */
  const onFinishContext = async (contextText, template) => {
    setIntroContext(contextText);
    setSelectedTemplate(template);

    setShowSpinner(true);
    try {
      const r = await introductionService.generate({
        TemplateId: template.id,
        Context: contextText,
        Recipients: selectedContacts.map((c) => c.id),
      });

      setTemplateText(r.htmlBody);
      setTemplateSubject(r.subject);
      setCurrentPane('compose-intro');
    } catch (error) {
      console.error('Error generating introduction:', error);
      const msg =
        error.response?.data || t('intro_modal_error_generating_intro');
      setErrorMessage(msg);
    } finally {
      setShowSpinner(false);
    }
  };

  /**
   * Clear the state, but call the callback.
   */
  const cleanSelectedStateAndSubmit = (submittedContacts, subject, body) => {
    setSelectedContacts([]);
    setIntroContext(undefined);
    setSelectedTemplate(undefined);
    setCurrentPane('select-contacts');
    onIntroEditorSubmit(submittedContacts, subject, body);
  };

  const handleTemplateCalls = async () => {
    const response = await templateService.list();
    setTemplates(response);

    let subjectText;
    let bodyText;

    if (response.length === 0) {
      const subjectResponse = await fetch(TEMPLATE_SUBJECT);
      subjectText = await subjectResponse.text();

      const bodyResponse = await fetch(TEMPLATE_BODY);
      bodyText = await bodyResponse.text();
    } else {
      subjectText = response[0].subject;
      bodyText = response[0].body;
    }

    setTemplateSubject(subjectText);
    setTemplateText(bodyText);
  };

  useEffect(() => {
    if (loaded || !isOpen) return;
    handleTemplateCalls();
    setLoaded(true);
  }, [isOpen, loaded]);

  useEffect(() => {
    setErrorMessage(null);
  }, [currentPane, isOpen]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={null}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative overflow-hidden text-left bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:w-full sm:max-w-3xl custom-mobile-modal">
                <div className="float-right p-4">
                  <button type="button" onClick={onClose}>
                    <Icon
                      className="text-gray-600"
                      iconClass="text-gray-600 h-5 w-5"
                      icon={<XMarkIcon />}
                    />
                  </button>
                </div>
                <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                  {currentPane === 'select-contacts' && (
                    <SelectContactsPane
                      title={t('intro_modal_select_contacts_title')}
                      open={showSelectContacts}
                      contacts={contacts}
                      onNewContactSubmit={onNewContactSubmit}
                      onGoForward={() => setCurrentPane('intro-context')}
                      onFinishSelection={onFinishSelection}
                    />
                  )}

                  {currentPane === 'intro-context' && (
                    <IntroContextPane
                      title={t('intro_modal_add_context_title')}
                      onGoBack={() => setCurrentPane('select-contacts')}
                      onGoForward={onFinishContext}
                      selectedContacts={selectedContacts}
                      templates={templates}
                      // Pass persisted values back down
                      initialContext={introContext}
                      initialTemplate={selectedTemplate}
                    />
                  )}

                  {currentPane === 'compose-intro' && (
                    <ComposeMessageModal
                      title={t('intro_modal_compose_intro_title')}
                      userData={userData}
                      open={showComposeIntro}
                      selectedContacts={selectedContacts}
                      onComposeSubmit={cleanSelectedStateAndSubmit}
                      defaultBody={templateText}
                      defaultSubject={templateSubject}
                      introContext={introContext}
                      onGoBack={() => setCurrentPane('intro-context')}
                      onClose={() => onClose()}
                      setShowSpinner={setShowSpinner}
                      setIntroModal={setIntroModal}
                    />
                  )}
                </div>
                {errorMessage && (
                  <div className="bg-red-50 p-4 border-b border-red-100 flex items-center justify-between">
                    <div className="flex items-center">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
                      <p className="text-sm text-red-700 font-medium">
                        {errorMessage}
                      </p>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

IntroModal.propTypes = {
  userData: propTypes.shape({}),
  isOpen: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
  contacts: propTypes.arrayOf(propTypes.shape({})).isRequired,
  onNewContactSubmit: propTypes.func.isRequired,
  onIntroEditorSubmit: propTypes.func.isRequired,
  setIntroModal: propTypes.func.isRequired,
  setShowSpinner: propTypes.func.isRequired,
};
